import React, { useEffect, useState } from "react";
import apiClient from "../../api/apiClient";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import "./ProductGrowingDetails.css";

export function GrowingDetailsDisplay({ details, options = {} }) {
    const entries = Object.entries(options).filter(([field]) => {
        const value = details[field];
        return value != null && value !== "" && (!Array.isArray(value) || value.length > 0);
    });
    if (!entries.length) return null;
    return (
        <dl className="pgd__values">
            {entries.map(([field, spec]) => (
                <div key={field}>
                    <dt>{spec.label}</dt>
                    <dd>
                        {Array.isArray(details[field])
                            ? details[field].join(", ")
                            : field === "trichome_density"
                              ? `${"◆".repeat(details[field])}${"◇".repeat(5 - details[field])} (${details[field]}/5)`
                              : details[field]}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

export default function ProductGrowingDetails({ productId, canEdit }) {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    useUnsavedChanges(editing, { message: "You have unsaved growing details. Leave this page?" });

    useEffect(() => {
        let cancelled = false;
        setData(null);
        setError("");
        setEditing(false);
        apiClient
            .get(`/products/${productId}/growing-details`)
            .then(({ data: result }) => {
                if (!cancelled) setData(result);
            })
            .catch((err) => {
                if (!cancelled) setError(err.response?.data?.error || "Unable to load growing details.");
            });
        return () => {
            cancelled = true;
        };
    }, [productId]);

    const save = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError("");
        try {
            const response = await apiClient.patch(`/products/${productId}/growing-details`, draft);
            setData((prev) => ({ ...prev, details: response.data }));
            setEditing(false);
        } catch (err) {
            setError(err.response?.data?.error || "Unable to save growing details.");
        } finally {
            setBusy(false);
        }
    };

    const upload = async (event) => {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setError("Pictures must be 5 MB or smaller.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await apiClient.post(`/products/upload-picture/${productId}`, formData);
            setData((prev) => ({ ...prev, pictures: [...prev.pictures, response.data] }));
            setDraft((prev) => ({ ...prev, wholesale_picture_url: response.data.picture_url }));
        } catch (err) {
            setError(err.response?.data?.error || "Unable to upload picture.");
        } finally {
            setBusy(false);
        }
    };

    const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
    return (
        <section className="coa-page__section pgd" aria-labelledby="growing-details-title">
            <div className="pgd__header">
                <h3 id="growing-details-title">Growing &amp; product characteristics</h3>
                {data && canEdit && !editing && (
                    <button
                        className="coa-page__detail-btn"
                        onClick={() => {
                            setDraft({ ...data.details });
                            setError("");
                            setEditing(true);
                        }}
                    >
                        Edit growing details
                    </button>
                )}
            </div>
            <p className="pgd__note">Optional grower-supplied details. Shown on the wholesale product page.</p>
            {error && (
                <p className="pgd__error" role="alert">
                    {error}
                </p>
            )}
            {!data && !error && <p role="status">Loading growing details…</p>}
            {data && !editing && (
                <>
                    <GrowingDetailsDisplay details={data.details} options={data.options} />
                    {!Object.values(data.details).some((value) =>
                        Array.isArray(value) ? value.length : value != null && value !== ""
                    ) && <p>No growing details added yet.</p>}
                    {data.details.wholesale_picture_url && (
                        <img
                            className="pgd__picture"
                            src={data.details.wholesale_picture_url}
                            alt="Wholesale product"
                        />
                    )}
                </>
            )}
            {data && editing && (
                <form onSubmit={save}>
                    <fieldset disabled={busy} className="pgd__fieldset">
                        <div className="pgd__grid">
                            {Object.entries(data.options)
                                .filter(([, spec]) => !spec.flowerOnly || data.product_type?.toLowerCase() === "flower")
                                .map(([field, spec]) =>
                                    spec.multiple ? (
                                        <fieldset key={field} className="pgd__tags">
                                            <legend>{spec.label}</legend>
                                            {spec.choices.map((choice) => (
                                                <label key={choice}>
                                                    <input
                                                        type="checkbox"
                                                        checked={(draft[field] || []).includes(choice)}
                                                        onChange={(event) =>
                                                            setField(
                                                                field,
                                                                event.target.checked
                                                                    ? [...(draft[field] || []), choice]
                                                                    : draft[field].filter((value) => value !== choice)
                                                            )
                                                        }
                                                    />
                                                    {choice}
                                                </label>
                                            ))}
                                        </fieldset>
                                    ) : (
                                        <label key={field} className="pgd__field">
                                            {spec.label}
                                            {spec.choices ? (
                                                <select
                                                    value={draft[field] ?? ""}
                                                    onChange={(event) =>
                                                        setField(
                                                            field,
                                                            event.target.value === ""
                                                                ? null
                                                                : field === "trichome_density"
                                                                  ? Number(event.target.value)
                                                                  : event.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="">Not supplied</option>
                                                    {spec.choices.map((choice) => (
                                                        <option key={choice} value={choice}>
                                                            {field === "trichome_density"
                                                                ? `${choice} / 5 diamonds`
                                                                : choice}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <textarea
                                                    rows={field === "cultivation_notes" ? 3 : 2}
                                                    maxLength={spec.maxLength}
                                                    value={draft[field] ?? ""}
                                                    onChange={(event) => setField(field, event.target.value || null)}
                                                />
                                            )}
                                        </label>
                                    )
                                )}
                        </div>
                        <p className="pgd__note">
                            Hybrid means mixed natural and artificial light. Diamond ratings are grower assessments on a
                            1–5 scale. Cultivation claims are not independently verified.
                        </p>
                        <label className="pgd__field">
                            Wholesale picture
                            <select
                                value={draft.wholesale_picture_url ?? ""}
                                onChange={(event) => setField("wholesale_picture_url", event.target.value || null)}
                            >
                                <option value="">No product picture selected</option>
                                {data.pictures.map((picture, index) => (
                                    <option key={picture.id} value={picture.picture_url}>
                                        Product picture {index + 1}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {draft.wholesale_picture_url && (
                            <img
                                className="pgd__picture"
                                src={draft.wholesale_picture_url}
                                alt="Selected wholesale product"
                            />
                        )}
                        <label className="pgd__field">
                            Upload a product picture (JPEG, PNG or WebP; max 5 MB)
                            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} />
                        </label>
                        <p className="pgd__note">
                            Uploads are added to the product immediately. Save to use the selected image on the
                            wholesale page.
                        </p>
                        <div className="pgd__actions">
                            <button className="coa-page__detail-btn coa-page__detail-btn--save" type="submit">
                                {busy ? "Saving…" : "Save growing details"}
                            </button>
                            <button
                                className="coa-page__detail-btn"
                                type="button"
                                onClick={() => {
                                    setEditing(false);
                                    setError("");
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </fieldset>
                </form>
            )}
        </section>
    );
}
